import { ReactNode, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import type { CatalogProvider, CatalogState, CatalogVehicleKind } from 'src/motornet/types';
import { useCatalogMakes, useCatalogModels } from './api';
import {
	activeFilterCount,
	CatalogFilterState,
	EMPTY_FILTERS,
	findOption,
	parseMinMatches,
	PROVIDER_LABELS,
	PROVIDERS,
	selectedOption,
} from './helpers';
import { Option, portalProps, useSelectLabels } from './selectStyles';

type Props = {
	filters: CatalogFilterState;
	search: string;
	onChange: (filters: CatalogFilterState, search: string) => void;
};

const FilterSection = ({ title, children }: { title: string; children: ReactNode }) => (
	<Col xs={12} lg={4}>
		<div className="small text-uppercase text-muted fw-semibold border-bottom pb-2 mb-3">{title}</div>
		<Row className="g-3">{children}</Row>
	</Col>
);

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
	<Col xs={12}>
		<Form.Label className="small mb-1">{label}</Form.Label>
		{children}
	</Col>
);

const CatalogFilters = ({ filters, search, onChange }: Props) => {
	const { t } = useTranslation();
	const [searchInput, setSearchInput] = useState(search);
	const [open, setOpen] = useState(false);
	const { make, model, ...rest } = filters;
	const facetFilters = { ...rest, search: search || undefined };
	const makes = useCatalogMakes(facetFilters, open);
	const models = useCatalogModels(facetFilters, make, open);
	const count = activeFilterCount(filters);

	const set = (patch: Partial<CatalogFilterState>) => onChange({ ...filters, ...patch }, search);
	const any = t('vehicle_association_filter_any', { defaultValue: 'Tutti' });
	const { noOptions } = useSelectLabels();
	const searchLabel = t('vehicle_association_search', {
		defaultValue: 'Cerca per marca, modello, versione, codice...',
	});

	const providerOptions: Option<CatalogProvider>[] = PROVIDERS.map((p) => ({ value: p, label: PROVIDER_LABELS[p] }));
	const minMatchesOption = (count: number): Option<number> => ({
		value: count,
		label: t('vehicle_association_min_matches_option', { count, defaultValue: 'Almeno {{count}}' }),
	});
	const minMatchesOptions = [2, 3, 4].map(minMatchesOption);
	const kindOptions: Option<CatalogVehicleKind>[] = [
		{ value: 'auto', label: t('vehicle_association_kind_auto', { defaultValue: 'Auto' }) },
		{ value: 'vcom', label: t('vehicle_association_kind_vcom', { defaultValue: 'LCV' }) },
	];
	const stateOptions: Option<CatalogState>[] = [
		{ value: 'unavailable', label: t('vehicle_association_state_unavailable', { defaultValue: 'Senza codice Motornet' }) },
		{ value: 'configurable', label: t('vehicle_association_state_configurable', { defaultValue: 'Configurabile' }) },
		{ value: 'quickable', label: t('vehicle_association_state_quickable', { defaultValue: 'Quotabile' }) },
	];
	const stockOptions: Option<boolean>[] = [
		{ value: true, label: t('vehicle_association_stock_in', { defaultValue: 'In stock' }) },
		{ value: false, label: t('vehicle_association_stock_out', { defaultValue: 'Senza stock' }) },
	];
	const autoRenewOptions: Option<boolean>[] = [
		{ value: true, label: t('yes', { defaultValue: 'Sì' }) },
		{ value: false, label: t('no', { defaultValue: 'No' }) },
	];
	const makeOptions: Option<string>[] = (makes.data?.items ?? []).map((m) => ({
		value: m.value,
		label: `${m.label} (${m.count})`,
	}));
	const modelOptions: Option<string>[] = (models.data?.items ?? []).map((m) => ({
		value: m.value,
		label: `${m.label} (${m.count})`,
	}));

	const submitSearch = () => onChange(filters, searchInput.trim());
	const reset = () => {
		setSearchInput('');
		onChange(EMPTY_FILTERS, '');
	};

	return (
		<Card className="mt-4">
			<Card.Body className="py-3">
				<div className="d-flex align-items-center gap-2">
					<Form
						className="flex-grow-1"
						onSubmit={(event) => {
							event.preventDefault();
							submitSearch();
						}}
					>
						<Form.Control
							type="search"
							value={searchInput}
							placeholder={searchLabel}
							aria-label={searchLabel}
							maxLength={200}
							onChange={(event) => {
								const value = event.target.value;
								setSearchInput(value);
								if (value === '' && search !== '') onChange(filters, '');
							}}
						/>
					</Form>
					{(count > 0 || search !== '' || searchInput !== '') && (
						<Button type="button" variant="light" className="text-nowrap" onClick={reset}>
							{t('reset_filters', { defaultValue: 'Reset filtri' })}
						</Button>
					)}
					<Button
						type="button"
						variant="outline-secondary"
						className="text-nowrap"
						aria-expanded={open}
						onClick={() => setOpen((value) => !value)}
					>
						{t('vehicle_association_advanced_filters', { defaultValue: 'Filtri avanzati' })}
						{count > 0 && (
							<Badge bg="secondary" className="ms-2">
								{count}
							</Badge>
						)}
					</Button>
					<Button type="button" variant="secondary" className="text-nowrap" onClick={submitSearch}>
						{t('search', { defaultValue: 'Cerca' })}
					</Button>
				</div>

				{open && (
					<Row className="g-4 mt-2 pt-3 border-top">
						<FilterSection title={t('vehicle_association_filter_group_match', { defaultValue: 'Match' })}>
							<FilterField label={t('vehicle_association_filter_providers', { defaultValue: 'Match su noleggiatore' })}>
								<Select<Option<CatalogProvider>, true>
									isMulti
									options={providerOptions}
									value={providerOptions.filter((o) => filters.providers?.includes(o.value))}
									placeholder={t('vehicle_association_any_provider', { defaultValue: 'Qualsiasi noleggiatore' })}
									{...portalProps}
									noOptionsMessage={noOptions}
									onChange={(options) => set({ providers: options.length ? options.map((o) => o.value) : undefined })}
								/>
							</FilterField>
							<FilterField
								label={t('vehicle_association_filter_min_matches', {
									defaultValue: 'Numero di noleggiatori con match',
								})}
							>
								<CreatableSelect<Option<number>>
									isClearable
									options={minMatchesOptions}
									value={filters.minMatches == null ? null : minMatchesOption(filters.minMatches)}
									placeholder={t('vehicle_association_any_number', { defaultValue: 'Qualsiasi numero' })}
									{...portalProps}
									isValidNewOption={(input) => {
										const count = parseMinMatches(input);
										return count != null && !minMatchesOptions.some((o) => o.value === count);
									}}
									formatCreateLabel={(input) => minMatchesOption(parseMinMatches(input) ?? 0).label}
									noOptionsMessage={() =>
										t('vehicle_association_min_matches_invalid', { defaultValue: 'Inserisci un numero da 1 a 4' })
									}
									onCreateOption={(input) => set({ minMatches: parseMinMatches(input) ?? undefined })}
									onChange={(option) => set({ minMatches: option?.value })}
								/>
							</FilterField>
						</FilterSection>
						<FilterSection title={t('vehicle_association_filter_group_catalog', { defaultValue: 'Catalogo' })}>
							<FilterField label={t('vehicle_association_filter_vehicle_kind', { defaultValue: 'Tipo veicolo' })}>
								<Select<Option<CatalogVehicleKind>>
									isClearable
									options={kindOptions}
									value={findOption(kindOptions, filters.vehicleKind)}
									placeholder={any}
									{...portalProps}
									onChange={(option) => set({ vehicleKind: option?.value })}
								/>
							</FilterField>
							<FilterField label={t('brand', { defaultValue: 'Marca' })}>
								<Select<Option<string>>
									isClearable
									options={makeOptions}
									value={selectedOption(makeOptions, make)}
									isLoading={makes.isFetching}
									placeholder={t('vehicle_association_any_make', { defaultValue: 'Tutte le marche' })}
									{...portalProps}
									noOptionsMessage={noOptions}
									onChange={(option) => set({ make: option?.value, model: undefined })}
								/>
							</FilterField>
							<FilterField label={t('model', { defaultValue: 'Modello' })}>
								<Select<Option<string>>
									isClearable
									options={modelOptions}
									value={selectedOption(modelOptions, model)}
									isDisabled={!make || models.isPlaceholderData}
									isLoading={models.isFetching}
									placeholder={
										make
											? t('vehicle_association_any_model', { defaultValue: 'Tutti i modelli' })
											: t('vehicle_association_select_make_first', { defaultValue: 'Seleziona una marca prima' })
									}
									{...portalProps}
									noOptionsMessage={noOptions}
									onChange={(option) => set({ model: option?.value })}
								/>
							</FilterField>
						</FilterSection>
						<FilterSection title={t('vehicle_association_filter_group_state', { defaultValue: 'Stato' })}>
							<FilterField label={t('vehicle_association_filter_stock', { defaultValue: 'Disponibilità' })}>
								<Select<Option<boolean>>
									isClearable
									options={stockOptions}
									value={findOption(stockOptions, filters.hasStock)}
									placeholder={any}
									{...portalProps}
									onChange={(option) => set({ hasStock: option?.value })}
								/>
							</FilterField>
							<FilterField label={t('vehicle_association_filter_state', { defaultValue: 'Codice Motornet' })}>
								<Select<Option<CatalogState>>
									isClearable
									options={stateOptions}
									value={findOption(stateOptions, filters.state)}
									placeholder={any}
									{...portalProps}
									onChange={(option) => set({ state: option?.value })}
								/>
							</FilterField>
							<FilterField
								label={t('vehicle_association_filter_matrix_auto_renew', {
									defaultValue: 'Rinnovo automatico matrice',
								})}
							>
								<Select<Option<boolean>>
									isClearable
									options={autoRenewOptions}
									value={findOption(autoRenewOptions, filters.matrixAutoRenew)}
									placeholder={any}
									{...portalProps}
									onChange={(option) => set({ matrixAutoRenew: option?.value })}
								/>
							</FilterField>
						</FilterSection>
					</Row>
				)}
			</Card.Body>
		</Card>
	);
};

export default CatalogFilters;
